// SCR-STAFF-303 — جزئیات نوبت (US-SCH-030، 031، 032، 033): وضعیت، زمان، خدمات، کانال، یادداشت، اطلاعات لغو و غیبت، تاریخچه و
// اقدام‌ها (جابه‌جایی با دیالوگ رزرو، لغو با دلیل، غیبت و اصلاح آن، CheckIn). مشاهده خدمت حساس در Audit ثبت می‌شود (سرور).
// AP-SCH-02: تأیید حضور تلفنی (US-SCH-034)، شروع و پایان خدمت (US-SCH-035)، بیعانه و معافیت با دلیل (US-SCH-023) و سری (US-SCH-022).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type AppointmentHistoryView, type AppointmentStatus, type AppointmentView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, ErrorState, faDigits, KeyValueList, LoadingState, PageHeader, ReasonDialog, toJalali } from '@phoenix/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { patientPath } from '../patients/common';
import { useLockPolicy } from './appointmentLock';
import { CancelAppointmentDialog, useAppointmentMutations } from './AppointmentActions';
import {
  AppointmentStatusChip,
  formatDay,
  formatTime,
  PriceText,
  schKeys,
  useActiveBranch,
  useBookingOptions,
  useCancelReasons,
  useReasonLabel,
} from './common';
import { useAppointmentsChanged } from './realtime';
import { useSchedulingActions } from './SchedulingActions';
import { PatientHeader } from '../patients/PatientHeader';

export const SCR_STAFF_303 = 'SCR-STAFF-303';

const ACTIVE: readonly AppointmentStatus[] = ['Booked', 'Confirmed', 'NeedsReschedule'];

function dateTime(instant: string | null | undefined, timeZone: string | undefined): string {
  return instant ? toJalali(instant, { timeZone, pattern: 'yyyy/MM/dd HH:mm' }) : '';
}

function HistoryItem({ entry, timeZone }: { entry: AppointmentHistoryView; timeZone: string | undefined }) {
  const { t } = useTranslation('sch');
  const moved =
    entry.oldStart && entry.newStart
      ? t('sch:history.moved', { from: dateTime(entry.oldStart, timeZone), to: dateTime(entry.newStart, timeZone) })
      : null;
  return (
    <ListItem disableGutters data-testid="appointment-history-item" data-action={entry.action}>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              {t(`sch:history.${entry.action}`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dateTime(entry.at, timeZone)}
            </Typography>
          </Stack>
        }
        secondary={[
          entry.actorName ? t('sch:history.by', { name: entry.actorName }) : t('sch:history.system'),
          moved,
          entry.reason,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
    </ListItem>
  );
}

export function AppointmentDetailPage() {
  const { t } = useTranslation('sch');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const scheduling = useSchedulingActions();
  const { branchId } = useActiveBranch();
  const options = useBookingOptions(branchId);
  const reasons = useCancelReasons();
  const reasonLabel = useReasonLabel();
  const { checkIn, noShow, correctNoShow, confirm, start, complete, waiveDeposit } = useAppointmentMutations();
  const lock = useLockPolicy();
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [noShowOpen, setNoShowOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const query = useQuery({
    queryKey: schKeys.appointment(session?.tenantId, id),
    queryFn: (): Promise<AppointmentView> =>
      unwrap(tenantApi.GET('/api/v1/scheduling/appointments/{id}', { params: { path: { id } } })),
    enabled: id !== '',
  });
  const appointment = query.data;
  const timeZone = options.data?.timeZone;

  useAppointmentsChanged((message) => {
    if (message.appointmentIds.includes(id)) {
      void queryClient.invalidateQueries({ queryKey: schKeys.appointment(session?.tenantId, id) });
    }
  });

  if (query.isPending) {
    return <LoadingState rows={6} />;
  }
  if (query.error || !appointment) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const full = access.accessMode === 'Full';
  const active = ACTIVE.includes(appointment.status);
  const isToday = options.data?.today === appointment.businessDate;
  const movementLocked = lock.isLocked(appointment.status) && !lock.canOverride;
  const can = {
    checkIn:
      full && access.can('sch.appointment.checkin') && isToday && (appointment.status === 'Booked' || appointment.status === 'Confirmed'),
    noShow: full && access.can('sch.appointment.mark_no_show') && active && appointment.possibleNoShow,
    correct:
      full &&
      access.can('sch.appointment.mark_no_show') &&
      appointment.status === 'NoShow' &&
      (!appointment.noShowCorrectableUntil || new Date(appointment.noShowCorrectableUntil).getTime() > Date.now()),
    reschedule: (scheduling?.canReschedule ?? false) && (active || appointment.status === 'PendingDeposit'),
    cancel: full && access.can('sch.appointment.cancel') && (active || appointment.status === 'PendingDeposit'),
    confirm: full && access.can('sch.appointment.update') && appointment.status === 'Booked',
    start: full && (access.can('sch.appointment.checkin') || access.can('sch.schedule.view_own')) && appointment.status === 'CheckedIn',
    complete: full && (access.can('sch.appointment.checkin') || access.can('sch.schedule.view_own')) && appointment.status === 'InProgress',
    waive: full && access.can('sch.appointment.override') && appointment.status === 'PendingDeposit',
  };
  const first = appointment.items[0];
  const reason = reasons.data?.find((item) => item.code === appointment.cancelReasonCode);
  const error = checkIn.error ?? noShow.error ?? correctNoShow.error ?? confirm.error ?? start.error ?? complete.error;

  return (
    <Stack data-screen-id={SCR_STAFF_303} spacing={2}>
      <PageHeader
        titleKey="sch:detail.title"
        badges={<AppointmentStatusChip status={appointment.status} possibleNoShow={appointment.possibleNoShow && active} />}
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {can.checkIn && (
              <Button
                variant="contained"
                onClick={() => checkIn.mutate(appointment.id, { onSuccess: () => setToast(t('sch:detail.checked_in')) })}
                disabled={checkIn.isPending}
                data-testid="detail-check-in"
              >
                {t('sch:detail.check_in')}
              </Button>
            )}
            {can.confirm && (
              <Button
                variant="outlined"
                onClick={() => confirm.mutate(appointment.id, { onSuccess: () => setToast(t('sch:detail.confirmed')) })}
                disabled={confirm.isPending}
                data-testid="detail-confirm"
              >
                {t('sch:detail.confirm')}
              </Button>
            )}
            {can.start && (
              <Button variant="contained" onClick={() => start.mutate(appointment.id)} disabled={start.isPending} data-testid="detail-start">
                {t('sch:detail.start')}
              </Button>
            )}
            {can.complete && (
              <Button variant="contained" color="success" onClick={() => complete.mutate(appointment.id)} disabled={complete.isPending} data-testid="detail-complete">
                {t('sch:detail.complete')}
              </Button>
            )}
            {can.waive && (
              <Button variant="outlined" onClick={() => setWaiveOpen(true)} data-testid="detail-waive-deposit">
                {t('sch:detail.waive_deposit')}
              </Button>
            )}
            {can.reschedule && !movementLocked && (
              <Button
                variant="outlined"
                onClick={() =>
                  scheduling?.openReschedule({
                    appointmentId: appointment.id,
                    rowVersion: appointment.rowVersion,
                    patient: { id: appointment.patientId, displayName: appointment.patientName ?? '', fileNumber: appointment.fileNumber },
                    serviceId: first?.serviceId ?? null,
                    quantity: Number(first?.quantity ?? 1),
                    providerId: first?.providerId ?? null,
                    date: appointment.businessDate,
                    seriesId: appointment.seriesId ?? null,
                    status: appointment.status,
                  })
                }
                data-testid="detail-reschedule"
              >
                {t('sch:detail.reschedule')}
              </Button>
            )}
            {can.reschedule && movementLocked && <LockedAction label={t('sch:detail.reschedule')} testId="detail-reschedule" />}
            {can.noShow && !movementLocked && (
              <Button color="warning" variant="outlined" onClick={() => setNoShowOpen(true)} data-testid="detail-no-show">
                {t('sch:detail.no_show')}
              </Button>
            )}
            {can.noShow && movementLocked && <LockedAction label={t('sch:detail.no_show')} testId="detail-no-show" />}
            {can.correct && (
              <Button variant="outlined" onClick={() => setCorrectOpen(true)} data-testid="detail-correct-no-show">
                {t('sch:detail.correct_no_show')}
              </Button>
            )}
            {can.cancel && !movementLocked && (
              <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)} data-testid="detail-cancel">
                {t('sch:detail.cancel')}
              </Button>
            )}
            {can.cancel && movementLocked && <LockedAction label={t('sch:detail.cancel')} testId="detail-cancel" />}
          </Stack>
        }
      />
      {access.can('pat.patient.view') && <PatientHeader patientId={appointment.patientId} compact />}
      {error ? <ErrorState error={error} compact /> : null}
      {appointment.possibleNoShow && active && <Alert severity="warning">{t('sch:detail.possible_no_show')}</Alert>}
      {appointment.status === 'NeedsReschedule' && appointment.rescheduleCause && (
        <Alert severity="warning" data-testid="detail-reschedule-cause">
          {t('sch:detail.reschedule_cause')}: {t(`sch:cause.${appointment.rescheduleCause}`)}
        </Alert>
      )}
      {can.correct && appointment.noShowCorrectableUntil && (
        <Alert severity="info">
          {t('sch:detail.correctable_until', { time: dateTime(appointment.noShowCorrectableUntil, timeZone) })}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent>
          <KeyValueList
            items={[
              {
                labelKey: 'sch:detail.patient',
                value: (
                  <Link component={RouterLink} to={patientPath(appointment.patientId)} data-testid="detail-patient">
                    {appointment.patientName}
                    {appointment.fileNumber ? ` · ${faDigits(appointment.fileNumber)}` : ''}
                  </Link>
                ),
              },
              {
                labelKey: 'sch:detail.time',
                value: (
                  <span data-testid="detail-time">
                    {formatDay(appointment.businessDate)} · {formatTime(appointment.start, timeZone)}–{formatTime(appointment.end, timeZone)}
                  </span>
                ),
              },
              { labelKey: 'sch:detail.channel', value: t(`sch:channel.${appointment.channel ?? 'Phone'}`) },
              ...(appointment.isOverbook ? [{ labelKey: 'sch:detail.overbook', value: '✓' }] : []),
              ...(appointment.seriesId
                ? [{ labelKey: 'sch:detail.series', value: t('sch:detail.series_index', { n: faDigits(Number(appointment.seriesIndex ?? 0)) }) }]
                : []),
              ...(appointment.confirmedAt
                ? [
                    {
                      labelKey: 'sch:detail.confirmed_at',
                      value: `${dateTime(appointment.confirmedAt, timeZone)}${appointment.confirmationSource ? ` · ${t(`sch:detail.confirmation_source.${appointment.confirmationSource}`, { defaultValue: appointment.confirmationSource })}` : ''}`,
                    },
                  ]
                : []),
              ...(appointment.depositAmount
                ? [
                    {
                      labelKey: 'sch:detail.deposit',
                      value: (
                        <Stack direction="row" spacing={1} component="span" alignItems="center" data-testid="detail-deposit">
                          <PriceText price={appointment.depositAmount} />
                          <span>
                            {appointment.depositPaidAt
                              ? t('sch:detail.deposit_paid', { time: dateTime(appointment.depositPaidAt, timeZone) })
                              : appointment.depositDueAt
                                ? t('sch:detail.deposit_due', { time: dateTime(appointment.depositDueAt, timeZone) })
                                : ''}
                          </span>
                        </Stack>
                      ),
                    },
                  ]
                : []),
              ...(appointment.depositWaiverReason ? [{ labelKey: 'sch:detail.deposit_waived', value: appointment.depositWaiverReason }] : []),
              ...(appointment.startedAt ? [{ labelKey: 'sch:detail.started_at', value: dateTime(appointment.startedAt, timeZone) }] : []),
              ...(appointment.waitMinutes !== null && appointment.waitMinutes !== undefined
                ? [{ labelKey: 'sch:detail.wait_minutes', value: faDigits(Number(appointment.waitMinutes)) }]
                : []),
              ...(appointment.completedAt ? [{ labelKey: 'sch:detail.completed_at', value: dateTime(appointment.completedAt, timeZone) }] : []),
              ...(appointment.notes ? [{ labelKey: 'sch:detail.notes', value: appointment.notes }] : []),
              ...(appointment.checkedInAt
                ? [{ labelKey: 'sch:detail.checked_in_at', value: dateTime(appointment.checkedInAt, timeZone) }]
                : []),
              ...(appointment.noShowAt ? [{ labelKey: 'sch:detail.no_show_at', value: dateTime(appointment.noShowAt, timeZone) }] : []),
              ...(appointment.status === 'Cancelled'
                ? [
                    {
                      labelKey: 'sch:detail.cancel_reason',
                      // علت سیستمی غیرفعال (مانند patient_deceased — AP-PAT-02) در فهرست انتخاب نیست؛ برچسب از i18n.
                      value: reason
                        ? reasonLabel(reason)
                        : appointment.cancelReasonCode
                          ? t(`sch:cancel_reason.${appointment.cancelReasonCode}`, { defaultValue: appointment.cancelReasonCode })
                          : '',
                    },
                    {
                      labelKey: 'sch:detail.cancelled_by',
                      value: (
                        <Stack direction="row" spacing={1} component="span">
                          <span>{appointment.cancelledBy === 'Center' ? t('sch:cancel.by_center') : t('sch:cancel.by_patient')}</span>
                          {appointment.lateCancel && (
                            <Chip size="small" color="warning" label={t('sch:detail.late_cancel')} data-testid="detail-late" />
                          )}
                        </Stack>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" component="h2" gutterBottom>
            {t('sch:detail.items')}
          </Typography>
          <Stack divider={<Divider flexItem />} spacing={1}>
            {appointment.items.map((item) => (
              <Stack key={item.id} direction={{ xs: 'column', sm: 'row' }} spacing={2} data-testid="detail-item">
                <Typography variant="body2" sx={{ flex: 2 }}>
                  {item.serviceMasked ? t('sch:day.masked_service') : item.serviceName}
                  {Number(item.quantity) > 1 ? ` × ${faDigits(item.quantity)}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {item.providerName}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {item.roomName ?? t('sch:booking.no_room')}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }} component="span">
                  <PriceText price={item.price} />
                </Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" component="h2">
            {t('sch:detail.history')}
          </Typography>
          <List dense data-testid="appointment-history">
            {appointment.history.map((entry, index) => (
              <HistoryItem key={`${entry.at}-${index}`} entry={entry} timeZone={timeZone} />
            ))}
          </List>
        </CardContent>
      </Card>

      <CancelAppointmentDialog
        open={cancelOpen}
        appointmentId={appointment.id}
        patientName={appointment.patientName}
        locked={lock.isLocked(appointment.status)}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          setToast(t('sch:detail.cancelled'));
        }}
      />
      {lock.isLocked(appointment.status) ? (
        <ReasonDialog
          open={noShowOpen}
          titleKey="sch:detail.no_show_title"
          messageKey="sch:detail.locked_no_show"
          confirmKey="sch:detail.no_show"
          minLength={10}
          destructive
          busy={noShow.isPending}
          error={noShow.error}
          onConfirm={(text) =>
            noShow.mutate(
              { id: appointment.id, overrideLock: true, overrideReason: text },
              {
                onSuccess: () => {
                  setNoShowOpen(false);
                  setToast(t('sch:detail.no_show_done'));
                },
              },
            )
          }
          onCancel={() => setNoShowOpen(false)}
        />
      ) : (
        <ConfirmDialog
          open={noShowOpen}
          titleKey="sch:detail.no_show_title"
          messageKey="sch:detail.no_show_message"
          messageValues={{ name: appointment.patientName ?? '' }}
          destructive
          busy={noShow.isPending}
          onConfirm={() =>
            noShow.mutate(
              { id: appointment.id },
              {
                onSuccess: () => {
                  setNoShowOpen(false);
                  setToast(t('sch:detail.no_show_done'));
                },
              },
            )
          }
          onCancel={() => setNoShowOpen(false)}
          error={noShow.error}
        />
      )}
      <ReasonDialog
        open={waiveOpen}
        titleKey="sch:detail.waive_deposit"
        destructive={false}
        busy={waiveDeposit.isPending}
        error={waiveDeposit.error}
        onConfirm={(text) =>
          waiveDeposit.mutate(
            { id: appointment.id, reason: text },
            {
              onSuccess: () => {
                setWaiveOpen(false);
                setToast(t('sch:detail.deposit_waived_done'));
              },
            },
          )
        }
        onCancel={() => setWaiveOpen(false)}
      />
      <ReasonDialog
        open={correctOpen}
        titleKey="sch:detail.correct_title"
        messageKey="sch:detail.correct_message"
        confirmKey="sch:detail.correct_no_show"
        destructive={false}
        busy={correctNoShow.isPending}
        error={correctNoShow.error}
        onConfirm={(text) =>
          correctNoShow.mutate(
            { id: appointment.id, reason: text },
            {
              onSuccess: () => {
                setCorrectOpen(false);
                setToast(t('sch:detail.corrected'));
              },
            },
          )
        }
        onCancel={() => setCorrectOpen(false)}
      />
      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)} data-testid="detail-toast">
          {toast}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

function LockedAction({ label, testId }: { label: string; testId: string }) {
  const { t } = useTranslation('sch');
  return (
    <Tooltip title={t('sch:detail.locked_action')}>
      <span>
        <Button variant="outlined" disabled data-testid={testId}>
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}
